import React, { useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import { useFormState, ComplaintDetailsFormFields, FormDataType } from 'contexts/FormProvider';
import styled from '@emotion/styled';
import { useContent, useContentContext } from '@qbe/dfc-content';
import { Button, CheckboxGroup, Dropdown, FormGroupLabel, TextField } from '@qbe/ads-core';
import { FormCard, Toggle } from '@qbe/ads-blocks-forms';

import { InformationPopUp, RadioGroup } from 'components';

import { FormStepContentProps } from 'types';

import { useScrollTop } from 'hooks/useScrollTop';
import { useCheckWindowCMS } from 'hooks/useCheckWindowCMS';
import useUpdateEffect from 'hooks/useUpdateEffect';

import {
  ClaimCategoryList,
  FinancialHardshipCategoryList,
  ProductTypeList,
  SalesCategoryList,
  ComplimentAreaList,
} from './enum';
import Attachment from 'components/Attachment/Attachment';
import FormSpacer from 'components/FormSpacer/FormSpacer';
import CurrencyTextField from 'components/CurrencyTextField/CurrencyTextField';

import validationSchema from './schema';

import { setFormCompleteEvent, setFormStartEvent } from 'hooks/useAnalytics';
import { yupResolver } from '@hookform/resolvers/yup';

export const CheckboxGroupStyled = styled(CheckboxGroup)`
  align-items: center;
`;

const ComplaintDetailsForm = ({ defaultValues, onComplete }: FormStepContentProps) => {
  useScrollTop();
  useCheckWindowCMS();
  const { formData } = useFormState();

  const { getTextContent } = useContent({
    context: useContentContext(),
  });
  // Localized content constants
  const pageHeading = getTextContent('complaint.form.heading');
  const detailsHeading = getTextContent(`${formData?.lodgement}.details.heading`);
  const productTitle = getTextContent(`${formData?.lodgement}.details.product`);
  const productTypeLabel = getTextContent('Feedback.details.productType.label');
  const compTypeSalesText = getTextContent('Feedback.details.compType.sales.text');
  const compTypeClaimsText = getTextContent('Feedback.details.compType.claims.text');
  const compTypeFinHardText = getTextContent('Feedback.details.compType.finHard.text');
  const complaintTypeTitle = getTextContent('complaint.details.complaintType');
  const complaintTypeInfoText = getTextContent('complaint.details.complaintType.infoText');
  const complaintCategoryTitle = getTextContent('complaint.details.complaintCategory');
  const complaintCategoryInfoText = getTextContent('complaint.details.complaintCategory.infoText');
  const compTypeToggleTitle = getTextContent(`${formData?.lodgement}.details.compType`);
  const complimentReasonLabel = getTextContent('Compliment.details.reason.label');
  const feedbackReasonLabel = getTextContent('Feedback.details.reason.label');
  const compAreaTitle = getTextContent(`${formData?.lodgement}.details.compArea`);
  const compDescriptionTitle = getTextContent(`${formData?.lodgement}.details.compDescription`);
  const compDescriptionPlaceholder = getTextContent(`${formData?.lodgement}.details.compDescription.placeHolder`);
  const desiredOutcomeTitle = getTextContent('complaint.details.customer.desiredOutcome');
  const desiredOutcomeLabel = getTextContent('complaint.details.customer.desiredOutcome.label');
  const amountInDisputeTitle = getTextContent('complaint.details.amountInDispute');
  const amountInDisputeLabel = getTextContent('complaint.details.amountInDispute.label');
  const submitLabel = getTextContent('Feedback.details.submit.label');

  const maxFileSizeLimit = typeof window._CMS_ === 'object' ? window._CMS_.MAX_FILE_UPLOAD_SIZE_IN_MB : 0;
  const enableAttachment = window._CMS_.ENABLE_FILE_UPLOAD;

  const form = useForm<ComplaintDetailsFormFields>({
    mode: 'onBlur',
    defaultValues,
    context: {
      maxFileSizeLimit,
    },

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver(validationSchema(formData?.lodgement) as any) as any,
  });

  const {
    control,
    setValue,
    handleSubmit,
    formState: { errors },
    watch,
    register,
    trigger: triggerValidation,
  } = form;

  const values = watch();
  const compType = watch('compType') || undefined;
  const compCategory = watch('compCategory') || [];

  const isCompTypeEnabled = !isEmpty(compType) ? true : false;

  const compCategoryOptions = {
    sales: SalesCategoryList,
    claims: ClaimCategoryList,
    financialHardshipApp: FinancialHardshipCategoryList,
    default: [],
  }[compType ?? 'default'];

  const getLodgementOption = (value: string) => formData?.lodgement === value;

  // Container for info box
  const formRef = useRef(null);

  // Controls enabling/disabling of form submission
  const isValid = validationSchema(formData?.lodgement).isValidSync(values);

  // Attachment
  React.useEffect(() => {
    register('files');
  }, [register]);

  const { files = [] } = values;

  const setFileValue = (newFiles: File[]) => {
    setValue('files', newFiles, { shouldValidate: true });
  };

  const updateFiles = (newFiles: FileList) => setFileValue([...files, ...Object.values(newFiles)]);
  const setFiles = (newFiles: File[]) => setFileValue(newFiles);

  React.useEffect(() => {
    register('productType');
    register('compArea');
  }, [register]);

  useUpdateEffect(() => {
    setValue('compCategory', []);
  }, [compType]);

  const onSubmit = (newData: Partial<FormDataType>) => {
    setFormCompleteEvent();
    onComplete(newData);
  };

  // moved above as constants

  return (
    <form onSubmit={handleSubmit(onSubmit)} ref={formRef} data-test="complaint-details-form">
      <FormCard variant="pageTitle" title={pageHeading} description="" />

      <FormSpacer type="pagesection" />

      <FormCard variant="sectionTitle" title={detailsHeading}>
        <FormGroupLabel title={productTitle} required={getLodgementOption('Complaint')} alignCenter />
        <FormSpacer type="input" />
        <Dropdown
          id="productType"
          data-track="productType"
          data-track-value="capture"
          error={!!errors.productType}
          helperText=""
          optionList={ProductTypeList.map((x) => ({ label: x, value: x }))}
          label={productTypeLabel}
          name="productType"
          defaultValue={watch('productType') ?? undefined}
          onChange={(event) => {
            setValue('productType', event.target.value as string);
            triggerValidation('productType');
            setFormStartEvent();
          }}
          onClose={() => triggerValidation('productType')}
        />

        {getLodgementOption('Complaint') && (
          <>
            <FormSpacer type="formgroup" />
            <Controller
              control={control}
              name="compType"
              rules={{ required: '' }}
              defaultValue=""
              render={({ field }) => (
                <RadioGroup
                  {...field}
                  direction="vertical"
                  required
                  styleVariant="skinny"
                  radioList={[
                    {
                      id: 'sales',
                      label: compTypeSalesText,
                      value: 'sales',
                    },
                    {
                      id: 'claims',
                      label: compTypeClaimsText,
                      value: 'claims',
                    },
                    {
                      id: 'financialHardshipApp',
                      label: compTypeFinHardText,
                      value: 'financialHardshipApp',
                    },
                  ]}
                  data-test="compType"
                  data-track="compType"
                  data-track-value="capture"
                  title={complaintTypeTitle}
                  titleSuffix={<InformationPopUp message={complaintTypeInfoText} />}
                />
              )}
            />
          </>
        )}
        <FormSpacer type="input" />
        {isCompTypeEnabled && compCategoryOptions && getLodgementOption('Complaint') && (
          <>
            <FormSpacer type="input" />
            <Controller
              control={control}
              name="compCategory"
              rules={{ required: '' }}
              render={({ field }) => (
                <CheckboxGroupStyled
                  {...field}
                  styleVariant="skinny"
                  checkboxList={compCategoryOptions.map((option) => ({
                    id: option.value,
                    name: 'compCategory',
                    label: option.text,
                    value: option.value,
                    disabled: !compCategory.includes(option.value) && compCategory.length === 2,
                  }))}
                  onChange={() => {
                    const newCategories = [
                      ...document.querySelectorAll<HTMLInputElement>('[name="compCategory"]:checked'),
                    ].map(({ value }) => value);
                    setValue('compCategory', newCategories);
                    field.onChange(newCategories);
                  }}
                  required
                  data-test="compCategory"
                  data-track="compCategory"
                  data-track-value="capture"
                  title={complaintCategoryTitle}
                  titleSuffix={<InformationPopUp message={complaintCategoryInfoText} />}
                />
              )}
            />
          </>
        )}
        {(getLodgementOption('Compliment') || getLodgementOption('Feedback')) && (
          <>
            <Controller
              name="compType"
              control={control}
              defaultValue={compType}
              rules={{ required: '' }}
              render={({ field }) => (
                <Toggle
                  {...field}
                  title={compTypeToggleTitle}
                  required
                  name="compType"
                  onChange={(e) => {
                    const value = e.target.value;
                    setValue('compType', value);
                    triggerValidation('compType');
                    field.onChange(value);
                  }}
                  optionList={[
                    {
                      text: compTypeClaimsText,
                      value: 'claims',
                    },
                    {
                      text: compTypeSalesText,
                      value: 'sales',
                    },
                  ].map((option) => ({
                    id: option.value,
                    value: option.value,
                    label: option.text ?? option.value,
                    defaultChecked: compType === option.value,
                    'data-test': 'compType',
                    'data-track': 'compType',
                    'data-track-value': 'capture',
                  }))}
                />
              )}
            />

            <FormGroupLabel title={compAreaTitle} required alignCenter />
            <FormSpacer type="input" />
            <Dropdown
              id="compArea"
              error={!!errors.compArea}
              label={getLodgementOption('Compliment') ? complimentReasonLabel : feedbackReasonLabel}
              name="compArea"
              data-track="compArea"
              data-track-value="capture"
              optionList={ComplimentAreaList.map((x) => ({ label: x, value: x }))}
              data-test="compArea"
              onChange={(event) => {
                const value = event.target.value as string;
                setValue('compArea', value);
                triggerValidation('compArea');
              }}
              onClose={() => triggerValidation('compArea')}
            />
          </>
        )}
        <FormSpacer type="input" />
        <FormGroupLabel title={compDescriptionTitle} required alignCenter />
        <FormSpacer type="input" />
        <Controller
          name="compDescription"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              id="compDescription"
              multiline
              value={field.value as string | undefined}
              placeholder={compDescriptionPlaceholder}
              label={compDescriptionPlaceholder}
              data-track="compDescription"
              data-track-value="mask"
              maxLength={4000}
              size="medium"
              data-test="compDescription"
              error={!!errors.compDescription?.message}
              helperText={errors.compDescription && String(errors.compDescription?.message)}
            />
          )}
        />
        {getLodgementOption('Complaint') && (
          <>
            <FormSpacer type="input" />
            <FormGroupLabel title={desiredOutcomeTitle} required alignCenter />
            <FormSpacer type="input" />
            <Controller
              name="desiredOutcome"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  multiline
                  id="desiredOutcome"
                  value={field.value as string | undefined}
                  placeholder={desiredOutcomeLabel}
                  label={desiredOutcomeLabel}
                  data-track="desiredOutcome"
                  data-track-value="mask"
                  maxLength={4000}
                  size="medium"
                  data-test="desiredOutcome"
                  error={!!errors.desiredOutcome?.message}
                  helperText={errors.desiredOutcome && String(errors.desiredOutcome?.message)}
                />
              )}
            />

            <FormSpacer type="input" />

            <FormGroupLabel title={amountInDisputeTitle} alignCenter />
            <FormSpacer type="input" />
            <Controller
              name="amountInDispute"
              control={control}
              render={({ field }) => (
                <CurrencyTextField
                  {...field}
                  id="amountInDispute"
                  value={field.value as string | undefined}
                  error={!!errors.amountInDispute}
                  label={amountInDisputeLabel}
                  data-track="amountInDispute"
                  data-track-value="mask"
                  helperText={get(errors, 'amountInDispute.message', null)}
                  data-test="amountInDispute"
                  maxLength={10}
                />
              )}
            />
          </>
        )}
        <FormSpacer type="input" />
        {getLodgementOption('Complaint') && (
          <Attachment
            enabled={enableAttachment}
            files={files}
            setFiles={setFiles}
            updateFiles={updateFiles}
            errorMessage={get(errors, 'files.message')?.toString()}
            maxFileSize={maxFileSizeLimit}
          />
        )}
        <FormSpacer type="formgroup" />
        <Button type="submit" label={submitLabel} disabled={!isValid} />
      </FormCard>
    </form>
  );
};

export default ComplaintDetailsForm;
